/**
 * "Shop by department" — the desktop mega menu, and the photo tile the phone drawer reuses.
 *
 * Layout (2026-10-10, owner: "looks too clustered … an image per category"): a left RAIL of every
 * top-level department; the one under the pointer/focus fills the right PANEL with its level-2
 * categories as a grid of photo tiles, each with up to three deeper categories as small text links
 * and a "Shop all" link. One department's children are on screen at a time, never the whole tree.
 *
 * Every category in the tree stays reachable: the rail lists all roots, the panel all of a root's
 * children, and a tile's "More in X" link reaches whatever is below its first three sub-links.
 *
 * Photos come from `/api/shop/nav` (worker/src/storefront.ts `navImagePlan`): a real product photo
 * from the category, or the owner's pick in Relay. `image: null` (e.g. Google's "Mature" branch)
 * draws a plain initial instead — never a stand-in picture.
 *
 * Keyboard: Enter/Space/↓ on the button opens it with focus on the first department; ↑/↓ (and
 * Home/End) move along the rail, → enters the panel, ← goes back to the rail, Esc closes and
 * returns focus to the button. Tab runs through everything in reading order.
 */
import { sizedSrc } from "./img";
import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from "react";
import type { NavNode } from "./types";
import { Go, type GoFn } from "./route";
import { deptRoute } from "./nav";
import { IconChevronDown, IconChevronRight, IconMenu } from "./icons";
import "./DeptMenu.css";

/** Sub-links under each tile before "More in X". */
const TILE_LINKS = 3;
/** Pointer must rest this long on a rail item before the panel switches (diagonal travel to the panel). */
const RAIL_INTENT_MS = 110;
/** Open/close delay for the whole menu on hover. */
const OPEN_INTENT_MS = 150;

/**
 * A thumbnail-sized source. Shopify-CDN urls take `width`; Relay's own `/media/` copies go through
 * Cloudflare Image Transformations (img.tsx `sizedSrc`).
 */
export function thumbSrc(url: string, width: number): string {
  return sizedSrc(url, width);
}

/**
 * A fixed-ratio square: the photo (lazy, decoded off-thread) or the category's initial on a plain
 * tint. The box is sized before anything loads, so nothing shifts.
 */
export function NavThumb({ node, size, className }: { node: Pick<NavNode, "name" | "image">; size: number; className?: string }) {
  const [failed, setFailed] = useState(false);
  const src = node.image && !failed ? node.image : null;
  return (
    <span className={`navthumb${className ? ` ${className}` : ""}`} aria-hidden="true">
      {src ? (
        <img src={thumbSrc(src, size * 2)} alt="" width={size} height={size} loading="lazy" decoding="async" onError={() => setFailed(true)} />
      ) : (
        <span className="navthumb-init">{node.name.trim().charAt(0).toUpperCase() || "•"}</span>
      )}
    </span>
  );
}

function Panel({ root, go, close, panelId }: { root: NavNode; go: GoFn; close: () => void; panelId: string }) {
  const headId = `${panelId}-h`;
  return (
    <section className="dm-panel" id={`${panelId}-p`} aria-labelledby={headId}>
      <header className="dm-head">
        <h3 id={headId}>{root.name}</h3>
        <Go to={deptRoute(root)} go={go} onNavigate={close} className="dm-all">
          Shop all {root.name} <span className="dm-n">({root.productCount})</span> <IconChevronRight size={16} />
        </Go>
      </header>
      {root.children.length === 0 ? (
        <Go to={deptRoute(root)} go={go} onNavigate={close} className="dm-solo">
          <NavThumb node={root} size={120} className="dm-tile-img" />
          <span>Browse all {root.productCount} products in {root.name}</span>
        </Go>
      ) : (
        <ul className="dm-grid">
          {root.children.map((c) => (
            <li key={c.id} className="dm-tile">
              <Go to={deptRoute(c)} go={go} onNavigate={close} className="dm-tile-main">
                <NavThumb node={c} size={120} className="dm-tile-img" />
                <span className="dm-tile-name">{c.name}</span>
              </Go>
              {c.children.length > 0 && (
                <ul className="dm-sub">
                  {c.children.slice(0, TILE_LINKS).map((g) => (
                    <li key={g.id}><Go to={deptRoute(g)} go={go} onNavigate={close}>{g.name}</Go></li>
                  ))}
                  {c.children.length > TILE_LINKS && (
                    <li className="dm-more"><Go to={deptRoute(c)} go={go} onNavigate={close}>More in {c.name} →</Go></li>
                  )}
                </ul>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

export function MegaMenu({ roots, go }: { roots: NavNode[]; go: GoFn }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement | null>(null);
  const btn = useRef<HTMLButtonElement | null>(null);
  const rail = useRef<HTMLUListElement | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const railTimer = useRef<number | undefined>(undefined);
  const focusRail = useRef<number | null>(null);
  const panelId = useId();
  const byHover = useRef(false);
  const later = (fn: () => void) => { window.clearTimeout(timer.current); timer.current = window.setTimeout(fn, OPEN_INTENT_MS); };
  const close = useCallback(() => {
    window.clearTimeout(timer.current);
    window.clearTimeout(railTimer.current);
    setOpen(false);
  }, []);

  const railLinks = () => Array.from(rail.current?.querySelectorAll<HTMLAnchorElement>("a.dm-rail-link") ?? []);
  const panelLinks = () => Array.from(wrap.current?.querySelectorAll<HTMLAnchorElement>(".dm-panel a") ?? []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { close(); btn.current?.focus(); } };
    const onDown = (e: MouseEvent) => { if (wrap.current && !wrap.current.contains(e.target as Node)) close(); };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => { document.removeEventListener("keydown", onKey); document.removeEventListener("mousedown", onDown); };
  }, [open, close]);
  useEffect(() => () => { window.clearTimeout(timer.current); window.clearTimeout(railTimer.current); }, []);
  /** Opened from the keyboard: put focus on the first department once the rail exists. */
  useEffect(() => {
    if (!open || focusRail.current === null) return;
    railLinks()[focusRail.current]?.focus();
    focusRail.current = null;
  }, [open]);

  const openByKey = (i: number) => {
    window.clearTimeout(timer.current);
    focusRail.current = i;
    setActive(i);
    setOpen(true);
  };

  const onRailKey = (e: ReactKeyboardEvent<HTMLUListElement>) => {
    const links = railLinks();
    const at = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (at < 0) return;
    const to = (i: number) => { e.preventDefault(); const n = (i + links.length) % links.length; setActive(n); links[n]?.focus(); };
    if (e.key === "ArrowDown") to(at + 1);
    else if (e.key === "ArrowUp") to(at - 1);
    else if (e.key === "Home") to(0);
    else if (e.key === "End") to(links.length - 1);
    else if (e.key === "ArrowRight") { e.preventDefault(); panelLinks()[0]?.focus(); }
  };
  const onPanelKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const links = panelLinks();
    const at = links.indexOf(document.activeElement as HTMLAnchorElement);
    if (at < 0) return;
    if (e.key === "ArrowLeft") { e.preventDefault(); railLinks()[active]?.focus(); }
    else if (e.key === "ArrowDown" || e.key === "ArrowRight") { e.preventDefault(); links[Math.min(at + 1, links.length - 1)]?.focus(); }
    else if (e.key === "ArrowUp") { e.preventDefault(); links[Math.max(at - 1, 0)]?.focus(); }
  };

  const current = roots[Math.min(active, Math.max(roots.length - 1, 0))];

  return (
    <div
      ref={wrap}
      className="mega-wrap"
      onPointerEnter={(e) => { if (e.pointerType === "mouse") later(() => { byHover.current = true; setOpen(true); }); }}
      onPointerLeave={(e) => { if (e.pointerType === "mouse") later(() => { byHover.current = false; setOpen(false); }); }}
      onBlur={(e) => { if (!wrap.current?.contains(e.relatedTarget as Node | null)) close(); }}
    >
      <button
        ref={btn} type="button" className="mega-btn" aria-expanded={open} aria-controls={panelId}
        onKeyDown={(e) => { if (e.key === "ArrowDown") { e.preventDefault(); openByKey(0); } }}
        onClick={(e) => {
          window.clearTimeout(timer.current);
          /* A mouse click right after the hover opened it must not close it again. */
          if (byHover.current) { byHover.current = false; setOpen(true); return; }
          /* Keyboard activation (detail 0) lands focus inside; a mouse click toggles. */
          if (e.detail === 0 && !open) { openByKey(0); return; }
          setOpen((o) => !o);
        }}
      >
        <IconMenu size={18} /> Shop by department <IconChevronDown />
      </button>
      {open && current && (
        <div id={panelId} className="dm" role="region" aria-label="Shop by department">
          <div className="wrap dm-in">
            <nav className="dm-rail" aria-label="Departments">
              <ul ref={rail} onKeyDown={onRailKey}>
                {roots.map((r, i) => (
                  <li
                    key={r.id}
                    className={i === active ? "on" : undefined}
                    onPointerEnter={(e) => {
                      if (e.pointerType !== "mouse") return;
                      window.clearTimeout(railTimer.current);
                      railTimer.current = window.setTimeout(() => setActive(i), RAIL_INTENT_MS);
                    }}
                    onPointerLeave={() => window.clearTimeout(railTimer.current)}
                  >
                    <Go to={deptRoute(r)} go={go} onNavigate={close} className="dm-rail-link" onFocus={() => setActive(i)}>
                      <NavThumb node={r} size={32} className="dm-rail-img" />
                      <span className="dm-rail-name">{r.name}</span>
                      <IconChevronRight size={16} />
                    </Go>
                  </li>
                ))}
              </ul>
              <Go to={{ name: "account", tab: "up" }} go={go} className="dm-trade" onNavigate={close}>
                Buying for a business? <b>Open a wholesale account →</b>
              </Go>
            </nav>
            <div className="dm-body" onKeyDown={onPanelKey}>
              <Panel root={current} go={go} close={close} panelId={panelId} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
